import { Card, Group, Stack, Text, Box, type CardProps } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import { Link } from 'react-router';
import type { ReactNode } from 'react';

interface KpiCardProps extends Omit<CardProps, 'children'> {
  label: ReactNode;
  value: ReactNode;
  accent?: ReactNode;
  chart?: ReactNode;
  delta?: ReactNode;
  description?: ReactNode;
  link?: string;
}

const linkIconCircle = (
  <Box
    aria-hidden
    style={{
      width: 32,
      height: 32,
      marginTop: -7,
      borderRadius: 999,
      background: 'var(--mantine-color-vdzGray-2)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <IconArrowUpRight size={17} stroke={2} color="var(--mantine-color-vdzGray-8)" />
  </Box>
);

export function KpiCard({
  label,
  value,
  accent,
  chart,
  delta,
  description,
  link,
  ...cardProps
}: KpiCardProps) {
  const labelBlock = (
    <Stack gap={4}>
      <Text size="sm" fw={700} fz={16}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="vdzGray.6">
          {description}
        </Text>
      )}
    </Stack>
  );

  const valueBlock = (
    <Group gap="sm" align="baseline">
      <Text fz={34} fw={700} lh={1} h={28}>
        {value}
      </Text>
      {delta}
    </Group>
  );

  if (chart) {
    return (
      <Card padding="lg" radius="lg" withBorder {...cardProps}>
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Stack gap={4}>
            {labelBlock}
            <Box mt={4}>{valueBlock}</Box>
          </Stack>
          <div style={{ flex: '1 1 60%', minWidth: 0 }}>{chart}</div>
        </Group>
      </Card>
    );
  }

  const cardBody = (
    <Card padding="md" radius="lg" withBorder h="100%" py="lg">
      <Stack gap={6} h="100%" justify="space-between" style={{ position: 'relative' }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={21}>
            {labelBlock}
            {valueBlock}
          </Stack>
          {link && linkIconCircle}
        </Group>
        {accent && (
          <Box style={{ position: 'absolute', right: 0, bottom: 0 }}>{accent}</Box>
        )}
      </Stack>
    </Card>
  );

  if (link) {
    return (
      <Box
        component={Link}
        to={link}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        {cardBody}
      </Box>
    );
  }

  return cardBody;
}
